import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
    IconGauge, IconReport, IconClock, IconLeave, IconHelp, IconChat,
    IconTeam, IconDonut, IconApprove, IconBoard, IconOrg, IconEmployees,
    IconBriefcase, IconHire, IconAsset,
    IconShield, IconDevices, IconBolt, IconExport,
    IconAttendance, IconStar, IconInterview,
} from './Icons'
import { SceneReport, SceneLeave } from './Scenes'
import './HomeContent.scss';

/* Everyone's day — the sidebar as every employee sees it */
const DAILY = [
    { icon: <IconGauge />, title: 'Dashboard', copy: 'Your work-report calendar, upcoming holidays, who on the team is on leave, and headcount by project — the day at a glance.' },
    { icon: <IconReport />, title: 'Work Reports', copy: 'Log the day project by project — activity, hours and notes — and download any date range as an Excel sheet.' },
    { icon: <IconClock />, title: 'Time Sheets', copy: 'Fill in the week, submit it once, and see who approved it — or why it came back.' },
    { icon: <IconAttendance />, title: 'Attendance', copy: 'Check in and out from the browser with your location, and see worked, break and idle time on a calendar.' },
    { icon: <IconLeave />, title: 'Leaves & Approvals', copy: 'Apply for leave or work from home in a few taps, and get an email and an in-app alert the moment it is decided.' },
    { icon: <IconStar />, title: 'Performance', copy: 'Rate yourself against weighted KRAs each quarter, then read your manager\'s rating and feedback side by side.' },
    { icon: <IconHelp />, title: 'Help Desk', copy: 'Raise a hardware, software, network or access ticket with a priority, and follow it through to resolved.' },
    { icon: <IconChat />, title: 'Messages', copy: 'Real-time one-to-one chat with typing, read receipts and online status — share images by paste or drag-and-drop.' },
];

/* Oversight — what team leads, managers and admins run the organisation with */
const MANAGE = [
    { icon: <IconTeam />, title: 'Team Work Reports', copy: 'Who has reported today and who has not, and every entry across the team — without asking anyone.' },
    { icon: <IconDonut />, title: 'Project Reports', copy: 'Running versus completed projects and team size per project, searchable by project, ID or manager.' },
    { icon: <IconApprove />, title: 'Approvals', copy: 'Leave requests and weekly timesheets waiting on you — approve with remarks, reject with a reason, or approve all at once.' },
    { icon: <IconBoard />, title: 'Manage Projects', copy: 'Create projects, assign the team, and close them out — ended projects drop off everyone\'s report form automatically.' },
    { icon: <IconOrg />, title: 'Manage Teams', copy: 'Group people under their manager, and set up the support teams employees can message directly.' },
    { icon: <IconEmployees />, title: 'Manage Employees', copy: 'Add, update and offboard people, and keep every employee profile current in one place.' },
];

/* Growth — hiring, skills and the kit people work on */
const GROW = [
    { icon: <IconBriefcase />, title: 'Open Jobs', copy: 'Publish a role with its skills, experience range, interview rounds and closing date, and switch it off once filled.' },
    { icon: <IconInterview />, title: 'Interview Schedules', copy: 'Move candidates from pool to scheduled to selected. Upload a resume and the name and email fill themselves.' },
    { icon: <IconHire />, title: 'Skill Search', copy: 'Search everyone by name, code or skill to find who already knows what you need — with experience and current project.' },
    { icon: <IconAsset />, title: 'Asset Management', copy: 'Keep a catalogue of laptops, phones and licences, issue them to people, and track what has been returned.' },
];

const REASONS = [
    { icon: <IconBolt />, title: 'Up and running fast', copy: 'Add your people, projects and leave types, and the team can start reporting the same day.' },
    { icon: <IconDevices />, title: 'Works where your team does', copy: 'Phone, laptop, office or home — the same screen, nothing to install.' },
    { icon: <IconShield />, title: 'Role-based throughout', copy: 'People see their own work; team leads and approvers see their team. Nothing leaks sideways.' },
    { icon: <IconExport />, title: 'Reports you can take away', copy: 'Download work reports for any date range as a styled Excel file, ready to share.' },
];

const STEPS = [
    { n: '01', title: 'Add your people', copy: 'Set up employees, their teams and managers. Everyone gets an email to activate their account.' },
    { n: '02', title: 'Shape it to your policy', copy: 'Leave types, projects, approvers and performance review templates — configured once.' },
    { n: '03', title: 'Start reporting', copy: 'The team checks in and submits from day one, and managers get their first full week without chasing.' },
];

const Cards = ({ items, cols }) => (
    <ul className={`hc-grid${cols ? ` hc-grid--${cols}` : ''}`}>
        {items.map((c) => (
            <li className="hc-card" key={c.title}>
                <span className="hc-icon">{c.icon}</span>
                <h3>{c.title}</h3>
                <p>{c.copy}</p>
            </li>
        ))}
    </ul>
);

const HomeContent = () => {
    const navigate = useNavigate();
    const start = () => navigate('/login');
    const contact = () => navigate('/contact');

    return (
        <div className="home-content">

            {/* 1 — LIGHT */}
            <section className="hc hc--light hc-modules" id="features">
                <div className="container">
                    <header className="hc-head">
                        <p className="hc-eyebrow">Every day</p>
                        <h2 className="hc-title">Everything your team touches daily</h2>
                        <p className="hc-lede">
                            From the morning check-in to the evening work report — attendance, timesheets,
                            leave, reviews, support and chat all sit behind one login, instead of across
                            spreadsheets, inboxes and chat threads.
                        </p>
                    </header>
                    <Cards items={DAILY} cols={4} />
                </div>
            </section>

            {/* 2 — DARK */}
            <section className="hc hc--dark hc-split">
                <div className="container">
                    <div className="hc-split-grid">
                        <div className="hc-split-copy">
                            <p className="hc-eyebrow">Daily reporting</p>
                            <h2 className="hc-title">A minute a day, not an afternoon</h2>
                            <p className="hc-lede">
                                Reporting only works when it is easier to do than to skip. Pick the project,
                                say what you did, submit — from wherever you are.
                            </p>
                            <ul className="hc-checks">
                                <li>Submit from your phone between meetings</li>
                                <li>Submitted, missed, leave and holidays on one calendar</li>
                                <li>Managers spot the gaps without asking anyone</li>
                            </ul>
                            <button type="button" className="hc-btn hc-btn--mint" onClick={start}>Get Started</button>
                        </div>
                        <div className="hc-split-art"><SceneReport /></div>
                    </div>
                </div>
            </section>

            {/* 3 — LIGHT */}
            <section className="hc hc--light hc-modules">
                <div className="container">
                    <header className="hc-head">
                        <p className="hc-eyebrow">Oversight</p>
                        <h2 className="hc-title">For the people running it</h2>
                        <p className="hc-lede">
                            Reports, approvals and the structure underneath them — projects, teams and
                            people — kept in step with each other.
                        </p>
                    </header>
                    <Cards items={MANAGE} cols={3} />
                </div>
            </section>

            {/* 4 — DARK */}
            <section className="hc hc--dark hc-split hc-split--reverse">
                <div className="container">
                    <div className="hc-split-grid">
                        <div className="hc-split-copy">
                            <p className="hc-eyebrow">Leave &amp; approvals</p>
                            <h2 className="hc-title">No more chasing sign-off over chat</h2>
                            <p className="hc-lede">
                                Set up your leave types once and let every request find its way — to the right
                                approver, with a clear decision and a record of why.
                            </p>
                            <ul className="hc-checks">
                                <li>Leave types set by your admin, including work from home</li>
                                <li>Requests routed to the approver, with remarks on every decision</li>
                                <li>Email and in-app alerts when a request is raised or decided</li>
                                <li>Team leave and upcoming holidays on the dashboard</li>
                            </ul>
                        </div>
                        <div className="hc-split-art"><SceneLeave /></div>
                    </div>
                </div>
            </section>

            {/* 5 — LIGHT */}
            <section className="hc hc--light hc-modules">
                <div className="container">
                    <header className="hc-head hc-head--center">
                        <p className="hc-eyebrow">Growing the team</p>
                        <h2 className="hc-title">Hiring, skills and kit</h2>
                        <p className="hc-lede">
                            The parts most tools leave out — the roles you are hiring for, the interviews
                            in flight, who knows what, and which laptop is on whose desk.
                        </p>
                    </header>
                    <Cards items={GROW} />
                </div>
            </section>

            {/* 6 — DARK */}
            <section className="hc hc--dark hc-reasons">
                <div className="container">
                    <header className="hc-head hc-head--center">
                        <p className="hc-eyebrow">Why teams stay</p>
                        <h2 className="hc-title">Light on process, heavy on clarity</h2>
                    </header>
                    <Cards items={REASONS} />
                </div>
            </section>

            {/* 7 — LIGHT */}
            <section className="hc hc--light hc-close">
                <div className="container">
                    <header className="hc-head hc-head--center">
                        <p className="hc-eyebrow">Getting started</p>
                        <h2 className="hc-title">Live in an afternoon</h2>
                    </header>
                    <ol className="hc-step-list">
                        {STEPS.map((s) => (
                            <li key={s.n}>
                                <span className="hc-step-n">{s.n}</span>
                                <h3>{s.title}</h3>
                                <p>{s.copy}</p>
                            </li>
                        ))}
                    </ol>
                    <div className="hc-cta-actions">
                        <button type="button" className="hc-btn hc-btn--primary" onClick={start}>Get Started</button>
                        <button type="button" className="hc-btn hc-btn--ghost" onClick={contact}>Contact Us</button>
                    </div>
                </div>
            </section>

        </div>
    )
}

export default HomeContent
